/**
 * useVoiceConversationEngine - Reusable voice conversation state machine
 * 
 * Extracted from the Symptom Checker VoiceConversation to be reusable across
 * features (SecureLink check-in, CMR review, discharge summary, etc.).
 * 
 * Handles all generic voice conversation infrastructure:
 * - STT (Web Speech API + Deepgram Flux for English; Deepgram Nova-3 streaming on
 *   browsers where Web Speech is broken, i.e. Microsoft Edge)
 * - TTS (OpenAI via Firebase or Deepgram Aura)
 * - Echo filtering and input gating
 * - Pause detection (timer-based + Flux EndOfTurn)
 * - Conversation log management
 * - Processing mutex for safe async question flow
 * 
 * Supports controlled state: when `controlledConversationLog` / `controlledQuestionsAsked`
 * are provided, the hook uses them instead of internal state (enables persistence
 * across remounts, lifted state in parent).
 * 
 * The "brain" (what questions to ask) is pluggable via onPauseDetected callback.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  acquireVoiceSession,
  releaseVoiceSession,
} from '../pageGuide/voiceSessionRegistry';
import {
  deepgramFluxService,
  FLUX_EVENTS,
  deepgramTTSService,
  geminiFlashTTSService,
  deepgramSTTService,
} from 'services/voice';
import {
  stopSpeaking as openAIStopSpeaking,
  unlockAudioContext,
  playPrefetchedAudio,
  TTS_PLAYBACK_STOPPED_ERROR,
} from 'services/ttsService';
import {
  speakUtterance as careStartSpeakUtterance,
  prefetchUtterance as careStartPrefetchUtterance,
  stopSpeaking as careStartStopSpeaking,
} from 'services/voice/careStartSpeakUtterance';
import {
  CARESTART_DEFAULT_TTS_MODEL,
  CARESTART_DEFAULT_TTS_SPEED,
} from 'features/carestart/careStartTtsConstants';

import {
  DEFAULT_PAUSE_DURATION_MS,
  FIRST_QUESTION_PAUSE_MS,
  FINAL_RESULT_PAUSE_MS,
  MAX_UTTERANCE_WAIT_MS,
  VOICE_CONFIG,
  ECHO_COOLDOWN_MS,
  POST_TTS_WEB_SPEECH_RESUME_MS,
  RESPONSE_DELAY_MS,
  SUPPORTED_LANGUAGES,
  useEchoFilter,
  useSymptomLanguage,
  usePauseDetection,
  useVoiceTTS,
  TTS_PROVIDER,
} from '../../features/patient/components/symptomCheck/voice-conversation';
import { scheduleVerbalEndConversationAck } from './scheduleVerbalEndConversationAck';
import { createVoiceLogAdapter } from './voiceLogAdapter';
import { isWebSpeechSttUnreliable } from './voiceConversationFlags';
import { createDeepgramMicCapture } from './deepgramMicCapture';
import { appendConversationTurn } from 'services/conversationSessionService';

/** Pause between sequential speech segments (e.g. unverified flag before clinical body). */
const SPEECH_SEGMENT_GAP_MS = 300;

const isPlaybackStoppedError = (error) =>
  error?.message === TTS_PLAYBACK_STOPPED_ERROR || error === TTS_PLAYBACK_STOPPED_ERROR;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Compare interim STT lines without resetting the pause timer on duplicate events. */
const normalizePauseText = (text) =>
  String(text ?? '')
    .toLowerCase()
    .replace(/[^\w\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * @param {Object} options
 * @param {function} options.onPauseDetected - Called when user finishes speaking.
 *   Receives ({ userTranscript, conversationLog, questionsAsked, context }).
 *   Must return { question: string|null, isComplete: boolean } or null to skip.
 * @param {string} [options.openingMessage] - First thing the AI says
 * @param {number} [options.pauseDuration] - Pause duration in ms
 * @param {string} [options.language='en-US'] - Language code
 * @param {function} [options.onLanguageChange] - Called when language changes
 * @param {string} [options.ttsProvider='openai'] - TTS provider
 * @param {string} [options.ttsVoice] - TTS voice id for current provider (user preference); used for OpenAI and Deepgram
 * @param {function} [options.onComplete] - Called with (conversationLog) when ended
 * @param {function} [options.onUserSpeech] - Called with each user utterance
 * @param {function} [options.onLogEntry] - Called with (newEntry, fullLog) on each log change
 * @param {function} [options.onLiveTranscript] - Called with (text) for live/in-progress STT (interim + accumulated); call with '' when committed
 * @param {Object} [options.context] - Opaque context passed to onPauseDetected
 * @param {boolean} [options.askFirstQuestionAfterOpening=false] - If true, after the opening message is spoken
 *   the engine immediately requests and speaks the first question (no wait for user response). Used for check-in.
 * @param {boolean} [options.askDisclaimerAfterOpening=true] - If true, the engine speaks the one-time
 *   medicolegal disclaimer from the brain before the opening line, then the opening question.
 * @param {function} [options.resolveTtsOptions] - Optional per-feature TTS overrides.
 *   Receives { text, deliveryProfile, phase } and returns { model, voice, speed, instructions } or null.
 *
 * Controlled state (optional - enables persistence across remounts):
 * @param {Array} [options.controlledConversationLog] - When provided, use this instead of internal log state
 * @param {function} [options.onConversationLogChange] - Setter for controlled log
 * @param {Array} [options.controlledQuestionsAsked] - When provided, use this instead of internal questions state
 * @param {function} [options.onQuestionsAskedChange] - Setter for controlled questions
 * @param {Object} [options.conversationSession] - When set, turns are mirrored to conversationSessions
 */
const useVoiceConversationEngine = ({
  onPauseDetected,
  openingMessage = 'Hello! How can I help you today?',
  pauseDuration = DEFAULT_PAUSE_DURATION_MS,
  language = 'en-US',
  onLanguageChange,
  ttsProvider = 'openai',
  ttsVoice,
  onComplete,
  onUserSpeech,
  onLogEntry,
  onLiveTranscript,
  context,
  askFirstQuestionAfterOpening = false,
  askDisclaimerAfterOpening = true,
  resolveTtsOptions,
  onMicError,
  enableBargeIn = false,
  onBargeIn,
  prepareSpeechText,
  allowBrowserTtsFallback = true,
  // Controlled state
  controlledConversationLog,
  onConversationLogChange,
  controlledQuestionsAsked,
  onQuestionsAskedChange,
  conversationSession = null,
} = {}) => {
  const conversationSessionRef = useRef(conversationSession);
  conversationSessionRef.current = conversationSession;
  const conversationTurnIndexRef = useRef(0);
  // =========================================================================
  // STATE (supports controlled & uncontrolled modes)
  // =========================================================================
  const [isActive, setIsActive] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');

  // Internal state (used when not in controlled mode)
  const [questionsAskedInternal, setQuestionsAskedInternal] = useState([]);
  const [conversationLogInternal, setConversationLogInternal] = useState([]);

  // Resolve controlled vs uncontrolled
  const questionsAsked = controlledQuestionsAsked !== undefined ? controlledQuestionsAsked : questionsAskedInternal;
  const conversationLog = controlledConversationLog !== undefined ? controlledConversationLog : conversationLogInternal;

  const setQuestionsAsked = useCallback((valueOrUpdater) => {
    if (onQuestionsAskedChange) {
      if (typeof valueOrUpdater === 'function') {
        onQuestionsAskedChange(prev => valueOrUpdater(prev));
      } else {
        onQuestionsAskedChange(valueOrUpdater);
      }
    } else {
      setQuestionsAskedInternal(valueOrUpdater);
    }
  }, [onQuestionsAskedChange]);

  const setConversationLog = useCallback((valueOrUpdater) => {
    if (onConversationLogChange) {
      if (typeof valueOrUpdater === 'function') {
        onConversationLogChange(prev => valueOrUpdater(prev));
      } else {
        onConversationLogChange(valueOrUpdater);
      }
    } else {
      setConversationLogInternal(valueOrUpdater);
    }
  }, [onConversationLogChange]);

  // Audio confirmation
  const [audioConfirmed, setAudioConfirmed] = useState(false);
  const audioConfirmedRef = useRef(false);

  // Input gating
  const acceptingInputRef = useRef(true);
  const [isAcceptingInput, setIsAcceptingInput] = useState(true);
  const [isProcessingInput, setIsProcessingInput] = useState(false);

  // Deepgram Flux state
  const [isFluxConnected, setIsFluxConnected] = useState(false);
  const [endConversationAcknowledged, setEndConversationAcknowledged] = useState(false);
  const [micError, setMicError] = useState(null);
  const onMicErrorRef = useRef(onMicError);
  const enableBargeInRef = useRef(enableBargeIn);
  const onBargeInRef = useRef(onBargeIn);
  const prepareSpeechTextRef = useRef(prepareSpeechText);
  const allowBrowserTtsFallbackRef = useRef(allowBrowserTtsFallback);
  const bargeInAbortRef = useRef(false);
  const [wasBargedIn, setWasBargedIn] = useState(false);

  // =========================================================================
  // REFS (avoid stale closures)
  // =========================================================================
  const recognitionRef = useRef(null);
  // Edge server-STT (Deepgram Nova-3) path — mic capture session + latest result handler.
  const deepgramCaptureRef = useRef(null);
  const deepgramSttStartingRef = useRef(false);
  const serverSttResultRef = useRef(null);
  const audioRef = useRef(null);
  const prefetchedOpeningAudioRef = useRef(null);
  const prefetchedOpeningTextRef = useRef('');
  const prefetchedOpeningKeyRef = useRef('');
  const isPrefetchingRef = useRef(false);
  const conversationScrollRef = useRef(null);
  const questionsAskedRef = useRef([]);
  const conversationLogRef = useRef([]);
  const handlePauseDetectedRef = useRef(null);
  const askQuestionRef = useRef(null);
  const hasStartedRef = useRef(false);
  const isActiveRef = useRef(false);
  const hasSpokeRecentlyRef = useRef(false);
  const isRecognitionRunningRef = useRef(false);
  const pendingEndOfTurnRef = useRef(null);
  const ttsProviderRef = useRef(ttsProvider);
  const ttsVoiceRef = useRef(ttsVoice);
  const isProcessingPauseRef = useRef(false);
  const adapterRef = useRef(null);
  const addToLogRef = useRef(null);
  /** Deferred user transcript to replay after disclaimer TTS completes. */
  const pendingDeferredTranscriptRef = useRef('');
  /** When set, handlePauseDetected uses this text instead of accumulator. */
  const deferredTranscriptOverrideRef = useRef('');
  /** True while session opening TTS plays — blocks barge-in echo from triggering a second reply. */
  const openingInProgressRef = useRef(false);
  const transcriptAccumulatorRef = useRef('');
  const interimTranscriptRef = useRef(''); // Latest interim (e.g. "yes"/"no") when browser never sends isFinal
  const utteranceWatchdogRef = useRef(null);
  const onPauseDetectedRef = useRef(onPauseDetected);
  const onLiveTranscriptRef = useRef(onLiveTranscript);
  const contextRef = useRef(context);
  const openingMessageRef = useRef(openingMessage);
  const stopConversationRef = useRef(null);
  const safetyTimerRef = useRef(null);
  const askFirstQuestionAfterOpeningRef = useRef(askFirstQuestionAfterOpening);
  const askDisclaimerAfterOpeningRef = useRef(askDisclaimerAfterOpening);
  const resolveTtsOptionsRef = useRef(resolveTtsOptions);

  // Keep controlled arrays in refs immediately (useEffect alone runs too late for first commit)
  questionsAskedRef.current = questionsAsked;
  conversationLogRef.current = conversationLog;

  // =========================================================================
  // HOOKS
  // =========================================================================
  const echoFilter = useEchoFilter();
  const symptomLanguage = useSymptomLanguage({
    defaultLanguage: language,
    onLanguageChange,
  });
  const pauseDetection = usePauseDetection({
    pauseDurationMs: pauseDuration,
    onPauseDetected: () => handlePauseDetectedRef.current?.(),
  });

  const clearUtteranceWatchdog = useCallback(() => {
    if (utteranceWatchdogRef.current) {
      clearTimeout(utteranceWatchdogRef.current);
      utteranceWatchdogRef.current = null;
    }
  }, []);

  const armUtteranceWatchdog = useCallback(() => {
    clearUtteranceWatchdog();
    utteranceWatchdogRef.current = setTimeout(() => {
      if (!isActiveRef.current || isProcessingPauseRef.current) return;
      const pending = (transcriptAccumulatorRef.current || interimTranscriptRef.current || '').trim();
      if (!pending) return;
      hasSpokeRecentlyRef.current = true;
      pauseDetection.clearPauseTimer();
      handlePauseDetectedRef.current?.();
    }, MAX_UTTERANCE_WAIT_MS);
  }, [clearUtteranceWatchdog, pauseDetection]);

  const schedulePauseAfterSpeech = useCallback((isFinalResult = false) => {
    pauseDetection.clearPauseTimer();
    const duration = isFinalResult
      ? FINAL_RESULT_PAUSE_MS
      : (questionsAskedRef.current.length === 0 ? FIRST_QUESTION_PAUSE_MS : pauseDuration);
    pauseDetection.startPauseTimer(duration);
    armUtteranceWatchdog();
  }, [pauseDetection, pauseDuration, armUtteranceWatchdog]);

  const releasePauseMutex = useCallback(() => {
    isProcessingPauseRef.current = false;
    setIsProcessingInput(false);
  }, []);

  const { isSpeaking, isSpeakingRef, setSpeaking } = useVoiceTTS();

  // Native Web Speech presence. Chrome exposes AND honors it; Edge exposes it but errors at
  // runtime (`language-not-supported` / `network`) and returns no transcript.
  const nativeWebSpeechAvailable = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  // INVARIANT: On Edge, capture speech with a server STT (Deepgram Nova-3 streaming) instead
  // of the broken Web Speech API, so the Classic engine keeps working. See voiceConversationFlags.js.
  const useServerStt = isWebSpeechSttUnreliable();
  // Web Speech is the actual transcript source only where the browser can run it (not Edge).
  const useWebSpeech = nativeWebSpeechAvailable && !useServerStt;
  // Flux (English turn detection) layers on the Web Speech path only — never the Edge server-STT path.
  const useDeepgramFlux = symptomLanguage.shouldUseFlux() && !useServerStt;
  // Voice is supported when we can use either Web Speech (Chrome) or server STT (Edge).
  const speechSupported = useServerStt || nativeWebSpeechAvailable;

  // Keep refs in sync with props
  useEffect(() => { ttsProviderRef.current = ttsProvider; }, [ttsProvider]);
  useEffect(() => { ttsVoiceRef.current = ttsVoice; }, [ttsVoice]);
  useEffect(() => { onPauseDetectedRef.current = onPauseDetected; }, [onPauseDetected]);
  useEffect(() => { contextRef.current = context; }, [context]);
  useEffect(() => { openingMessageRef.current = openingMessage; }, [openingMessage]);
  useEffect(() => { onLiveTranscriptRef.current = onLiveTranscript; }, [onLiveTranscript]);
  useEffect(() => { askFirstQuestionAfterOpeningRef.current = askFirstQuestionAfterOpening; }, [askFirstQuestionAfterOpening]);
  useEffect(() => { askDisclaimerAfterOpeningRef.current = askDisclaimerAfterOpening; }, [askDisclaimerAfterOpening]);
  useEffect(() => { resolveTtsOptionsRef.current = resolveTtsOptions; }, [resolveTtsOptions]);
  useEffect(() => { onMicErrorRef.current = onMicError; }, [onMicError]);
  useEffect(() => { enableBargeInRef.current = enableBargeIn; }, [enableBargeIn]);
  useEffect(() => { onBargeInRef.current = onBargeIn; }, [onBargeIn]);
  useEffect(() => { prepareSpeechTextRef.current = prepareSpeechText; }, [prepareSpeechText]);
  useEffect(() => { allowBrowserTtsFallbackRef.current = allowBrowserTtsFallback; }, [allowBrowserTtsFallback]);

  const interruptAssistantSpeech = useCallback(() => {
    bargeInAbortRef.current = true;
    try {
      careStartStopSpeaking();
    } catch (e) {
      /* ignore */
    }
    try {
      openAIStopSpeaking();
    } catch (e) {
      /* ignore */
    }
    try {
      deepgramTTSService.stop();
    } catch (e) {
      /* ignore */
    }
    try {
      geminiFlashTTSService.stop();
    } catch (e) {
      /* ignore */
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {
        /* ignore */
      }
    }
    setSpeaking(false);
    echoFilter.clearEchoCooldown?.();
    acceptingInputRef.current = true;
    setIsAcceptingInput(true);
    releasePauseMutex();
    setIsListening(true);
    setWasBargedIn(true);
    onBargeInRef.current?.();
    return true;
  }, [echoFilter, setSpeaking]);

  const maybeInterruptForUserSpeech = useCallback(() => {
    if (!enableBargeInRef.current || !isSpeakingRef.current) return false;
    return interruptAssistantSpeech();
  }, [interruptAssistantSpeech, isSpeakingRef]);

  const clearWasBargedIn = useCallback(() => {
    setWasBargedIn(false);
  }, []);

  const reportMicError = useCallback((message) => {
    if (!message) return;
    setMicError(message);
    onMicErrorRef.current?.(message);
  }, []);

  const clearMicError = useCallback(() => {
    setMicError(null);
  }, []);

  /** Prime mic permission before Web Speech / Deepgram capture (within Start gesture). */
  const ensureMicrophoneAccess = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      reportMicError(
        denied
          ? 'Microphone access was blocked. Allow the mic for this site in browser settings, then try again.'
          : 'Could not open the microphone. Check your device and try again.'
      );
      return false;
    }
  }, [reportMicError]);

  const resolveTtsOptionsForSpeak = useCallback(({ text, deliveryProfile, phase }) => {
    const fn = resolveTtsOptionsRef.current;
    if (typeof fn !== 'function') return null;
    try {
      return fn({ text, deliveryProfile, phase }) ?? null;
    } catch (err) {
      console.warn('[VoiceEngine] resolveTtsOptions failed:', err);
      return null;
    }
  }, []);

  const resolveAssistantTextForUser = useCallback(async (text) => {
    const baseText = String(text || '').trim();
    if (!baseText) return '';
    if (!symptomLanguage.needsTranslation) return baseText;
    try {
      return await symptomLanguage.translateQuestion(baseText);
    } catch (err) {
      console.warn('[VoiceEngine] Translation for assistant text failed:', err);
      return baseText;
    }
  }, [symptomLanguage]);

  // =========================================================================
  // PREFETCH OPENING AUDIO
  // =========================================================================
  useEffect(() => {
    const doPrefetch = async () => {
      const voice = ttsVoiceRef.current ?? VOICE_CONFIG.voice;
      const lang = symptomLanguage.language || 'en-US';
      const text = await resolveAssistantTextForUser(openingMessageRef.current);
      const openingTtsOptions = resolveTtsOptionsForSpeak({
        text,
        deliveryProfile: 'conversational',
        phase: 'opening',
      });
      const prefetchKey = [
        ttsProvider,
        voice || '',
        openingMessageRef.current || '',
        lang,
        openingTtsOptions?.model || '',
        openingTtsOptions?.voice || '',
        openingTtsOptions?.instructions || '',
      ].join('|');
      if (prefetchedOpeningKeyRef.current !== prefetchKey) {
        prefetchedOpeningAudioRef.current = null;
        prefetchedOpeningTextRef.current = '';
        prefetchedOpeningKeyRef.current = prefetchKey;
      }
      if (prefetchedOpeningAudioRef.current || isPrefetchingRef.current) return;
      isPrefetchingRef.current = true;
      try {
        if (!text) { isPrefetchingRef.current = false; return; }
        const speed = openingTtsOptions?.speed ?? VOICE_CONFIG.speed ?? 0.95;
        if (ttsProvider === 'openai' || ttsProvider === 'gemini_flash_tts') {
          const audioBlob = await careStartPrefetchUtterance(text, {
            language: lang,
            provider: ttsProvider === 'gemini_flash_tts' ? 'gemini_flash_tts' : 'openai',
            voice: openingTtsOptions?.voice || voice || (ttsProvider === 'gemini_flash_tts' ? 'Aoede' : VOICE_CONFIG.voice),
            model: openingTtsOptions?.model || CARESTART_DEFAULT_TTS_MODEL,
            speed: openingTtsOptions?.speed ?? speed ?? CARESTART_DEFAULT_TTS_SPEED,
            instructions: openingTtsOptions?.instructions,
          });
          if (audioBlob) {
            prefetchedOpeningAudioRef.current = audioBlob;
            prefetchedOpeningTextRef.current = text;
          }
        } else if (ttsProvider === 'deepgram') {
          deepgramTTSService.configure({ voice });
          await deepgramTTSService.generateSpeech(text, { voice, useCache: true });
        }
      } catch (error) {
        console.warn('[VoiceEngine] Failed to prefetch opening audio:', error);
      } finally {
        isPrefetchingRef.current = false;
      }
    };
    doPrefetch();
  }, [ttsProvider, ttsVoice, openingMessage, symptomLanguage.language, resolveAssistantTextForUser, resolveTtsOptionsForSpeak]);

  // =========================================================================
  // PRE-CONNECT DEEPGRAM FLUX
  // =========================================================================
  useEffect(() => {
    if (!useDeepgramFlux) return;
    const preConnect = async () => {
      if (deepgramFluxService.isConnected()) { setIsFluxConnected(true); return; }
      try {
        const connected = await deepgramFluxService.connect();
        if (connected) setIsFluxConnected(true);
      } catch (error) {
        console.warn('[VoiceEngine] Flux pre-connection error:', error);
      }
    };
    preConnect();
  }, [useDeepgramFlux]);

  // =========================================================================
  // CONVERSATION LOG
  // =========================================================================

  const addToLog = useCallback((speaker, text) => {
    const newEntry = { speaker, text, timestamp: Date.now() };
    const currentLog = conversationLogRef.current;
    const newLog = [...currentLog, newEntry];
    conversationLogRef.current = newLog;
    setConversationLog(newLog);

    const session = conversationSessionRef.current;
    if (session?.sessionId && session?.organizationId && text?.trim()) {
      appendConversationTurn({
        ...session,
        turn: {
          turnIndex: conversationTurnIndexRef.current,
          speaker: speaker === 'user' ? 'user' : 'system',
          text: text.trim(),
        },
      }).catch((err) => {
        console.warn('[VoiceEngine] conversation turn write failed', err?.message || err);
      });
      conversationTurnIndexRef.current += 1;
    }

    // Notify consumer of new entry
    setTimeout(() => {
      onLogEntry?.(newEntry, newLog);
    }, 0);
  }, [setConversationLog, onLogEntry]);

  addToLogRef.current = addToLog;

  if (!adapterRef.current) {
    adapterRef.current = createVoiceLogAdapter({
      onCommit: (entry) => {
        addToLogRef.current?.(entry.speaker, entry.text);
      },
      onLivePartial: () => {
        // Classic has no streaming partials — no-op
      },
      onBeforeCommit: () => {
        // No DDX dirty flag needed in Classic
      },
    });
  }

  const addUserToLog = useCallback(async (userText) => {
    if (!userText || !userText.trim()) return;
    const toLog = symptomLanguage.needsTranslation
      ? await symptomLanguage.translateToEnglish(userText)
      : userText.trim();
    adapterRef.current.commitUserTurn(toLog);
    setTimeout(() => { onUserSpeech?.(toLog); }, 0);
  }, [symptomLanguage, onUserSpeech]);

  // =========================================================================
  // TTS
  // =========================================================================

  const speakWithPreferredProvider = useCallback(async (text, options = {}) => {
    const provider = ttsProviderRef.current;
    const preferredVoice = ttsVoiceRef.current ?? VOICE_CONFIG.voice;
    const preferredSpeed = VOICE_CONFIG.speed ?? 0.95;
    const {
      onStart,
      onEnd,
      onError,
      voice = preferredVoice,
      speed = preferredSpeed,
      resolvedText,
      preserveExactText = false,
      deliveryProfile,
      phase,
      ttsOptions: explicitTtsOptions,
      segmentPosition = 'only',
    } = options;

    const isFirstSegment = segmentPosition === 'only' || segmentPosition === 'first';
    const isFinalSegment = segmentPosition === 'only' || segmentPosition === 'last';

    if (!text || text.trim().length === 0) {
      console.warn('[VoiceEngine] Empty text passed to TTS');
      onEnd?.();
      return null;
    }

    let textToSpeak;
    if (preserveExactText) {
      textToSpeak =
        typeof resolvedText === 'string' && resolvedText.trim()
          ? resolvedText.trim()
          : String(text || '').trim();
    } else {
      textToSpeak =
        typeof resolvedText === 'string' && resolvedText.trim()
          ? resolvedText.trim()
          : await resolveAssistantTextForUser(text);
    }

    const prepare = prepareSpeechTextRef.current;
    if (typeof prepare === 'function') {
      textToSpeak = prepare(textToSpeak) ?? textToSpeak;
    }

    echoFilter.addSystemUtterance(textToSpeak);
    if (isFirstSegment && !enableBargeInRef.current) {
      acceptingInputRef.current = false;
      setIsAcceptingInput(false);
    }

    const langCode = symptomLanguage.language || 'en-US';
    const ttsOptions =
      explicitTtsOptions ??
      resolveTtsOptionsForSpeak({ text: textToSpeak, deliveryProfile, phase });

    const handleTTSStart = () => {
      echoFilter.startEchoCooldown();
      onStart?.();
    };

    const handleTTSEnd = () => {
      if (bargeInAbortRef.current) {
        onEnd?.();
        return;
      }
      echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
      if (isFinalSegment) {
        setTimeout(async () => {
          acceptingInputRef.current = true;
          setIsAcceptingInput(true);
          if (pendingEndOfTurnRef.current && hasSpokeRecentlyRef.current && isActiveRef.current) {
            const pending = pendingEndOfTurnRef.current;
            pendingEndOfTurnRef.current = null;
            if (pending.transcript) await addUserToLog(pending.transcript);
            if (handlePauseDetectedRef.current) handlePauseDetectedRef.current();
          }
        }, ECHO_COOLDOWN_MS);
      }
      onEnd?.();
    };

    const handleTTSError = (err) => {
      if (bargeInAbortRef.current || isPlaybackStoppedError(err)) {
        onError?.(err);
        return;
      }
      echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
      if (isFinalSegment) {
        setTimeout(async () => {
          acceptingInputRef.current = true;
          setIsAcceptingInput(true);
          if (pendingEndOfTurnRef.current && hasSpokeRecentlyRef.current && isActiveRef.current) {
            const pending = pendingEndOfTurnRef.current;
            pendingEndOfTurnRef.current = null;
            if (pending.transcript) await addUserToLog(pending.transcript);
            if (handlePauseDetectedRef.current) handlePauseDetectedRef.current();
          }
        }, ECHO_COOLDOWN_MS);
      }
      onError?.(err);
    };

    if (provider === TTS_PROVIDER.DEEPGRAM) {
      try {
        deepgramTTSService.configure({ voice });
        deepgramTTSService.setHandlers({
          onStart: handleTTSStart,
          onEnd: handleTTSEnd,
          onError: (err) => { console.error('[VoiceEngine] Deepgram TTS error:', err); handleTTSError(err); },
        });
        await deepgramTTSService.speak(textToSpeak, { voice });
        return { provider: 'deepgram' };
      } catch (error) {
        console.error('[VoiceEngine] Deepgram TTS error:', error);
        handleTTSError(error);
        throw error;
      }
    }

    const speakViaBrowserFallback = () =>
      new Promise((resolve, reject) => {
        if (!allowBrowserTtsFallbackRef.current || !('speechSynthesis' in window)) {
          reject(new Error('browser_tts_unavailable'));
          return;
        }
        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.rate = 0.95;
        utterance.onstart = () => handleTTSStart();
        utterance.onend = () => {
          handleTTSEnd();
          resolve({ provider: 'browser' });
        };
        utterance.onerror = (event) => {
          const err = new Error(event?.error || 'browser_tts_error');
          handleTTSError(err);
          reject(err);
        };
        window.speechSynthesis.speak(utterance);
      });

    if (
      provider === TTS_PROVIDER.GEMINI_FLASH_TTS ||
      provider === TTS_PROVIDER.OPENAI
    ) {
      try {
        unlockAudioContext();
        const result = await careStartSpeakUtterance({
          text: textToSpeak,
          language: langCode,
          provider:
            provider === TTS_PROVIDER.GEMINI_FLASH_TTS
              ? 'gemini_flash_tts'
              : 'openai',
          voice:
            ttsOptions?.voice ||
            voice ||
            (provider === TTS_PROVIDER.GEMINI_FLASH_TTS ? 'Aoede' : preferredVoice),
          model: ttsOptions?.model || CARESTART_DEFAULT_TTS_MODEL,
          speed: ttsOptions?.speed ?? speed ?? CARESTART_DEFAULT_TTS_SPEED,
          instructions: ttsOptions?.instructions,
          onStart: handleTTSStart,
          onEnd: handleTTSEnd,
          onError: (err) => {
            if (!isPlaybackStoppedError(err)) {
              console.error('[VoiceEngine] CareStart TTS error:', err);
            }
            handleTTSError(err);
          },
        });
        if (result?.success) {
          return { provider: result.provider || provider };
        }
        if (result?.reason === TTS_PLAYBACK_STOPPED_ERROR || bargeInAbortRef.current) {
          throw new Error(TTS_PLAYBACK_STOPPED_ERROR);
        }
        throw new Error(result?.reason || 'TTS generation returned no audio');
      } catch (error) {
        if (bargeInAbortRef.current || isPlaybackStoppedError(error)) {
          throw error;
        }
        console.error('[VoiceEngine] Cloud TTS error:', error);
        if (allowBrowserTtsFallbackRef.current) {
          try {
            return await speakViaBrowserFallback();
          } catch (fallbackError) {
            handleTTSError(fallbackError);
            throw fallbackError;
          }
        }
        handleTTSError(error);
        throw error;
      }
    }

    const err = new Error(`Unsupported TTS provider: ${provider}`);
    handleTTSError(err);
    throw err;
  }, [echoFilter, symptomLanguage.language, addUserToLog, resolveAssistantTextForUser, resolveTtsOptionsForSpeak]);

  // =========================================================================
  // SPEECH RECOGNITION (Web Speech API)
  // =========================================================================

  useEffect(() => {
    // Only create Web Speech recognition where the browser can actually run it.
    // On Edge (useServerStt) this is skipped — the Deepgram streaming path handles STT.
    if (!useWebSpeech) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = symptomLanguage.getWebSpeechLanguage();

    recognition.onstart = () => { isRecognitionRunningRef.current = true; };
    recognition.onaudiostart = () => {
      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
    };
    recognition.onspeechstart = () => {
      if (maybeInterruptForUserSpeech()) {
        return;
      }
      if (!audioConfirmedRef.current) {
        setAudioConfirmed(true);
        audioConfirmedRef.current = true;
      }
    };

    recognition.onresult = (event) => {
      if (openingInProgressRef.current) return;
      if (!acceptingInputRef.current && !(enableBargeInRef.current && isSpeakingRef.current)) return;
      if (isSpeakingRef.current && !enableBargeInRef.current) return;
      if (echoFilter.isInCooldown()) return;
      if (isProcessingPauseRef.current) return;

      const previousInterim = interimTranscriptRef.current;
      let finalTranscript = '';
      let latestInterim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript?.trim() || '';
        if (result.isFinal) {
          finalTranscript += transcript;
        }
        if (transcript) {
          latestInterim = transcript;
        }
      }
      if (latestInterim) {
        interimTranscriptRef.current = latestInterim;
      }
      if (event.results.length > 0 && event.results[event.results.length - 1].isFinal) {
        interimTranscriptRef.current = '';
      }

      if (!finalTranscript && !latestInterim) return;

      const interimChanged =
        Boolean(latestInterim) &&
        !finalTranscript &&
        normalizePauseText(latestInterim) !== normalizePauseText(previousInterim);

      if (finalTranscript) {
        transcriptAccumulatorRef.current = (transcriptAccumulatorRef.current + ' ' + finalTranscript).trim();
        hasSpokeRecentlyRef.current = true;
      } else if (latestInterim) {
        hasSpokeRecentlyRef.current = true;
      }

      const liveText = (transcriptAccumulatorRef.current + (interimTranscriptRef.current ? ' ' + interimTranscriptRef.current : '')).trim();
      onLiveTranscriptRef.current?.(liveText);

      if (finalTranscript || interimChanged) {
        schedulePauseAfterSpeech(Boolean(finalTranscript));
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') {
        if (event.error === 'aborted') isRecognitionRunningRef.current = false;
      } else {
        console.error('[VoiceEngine] Recognition error:', event.error);
        setIsListening(false);
        isRecognitionRunningRef.current = false;
        if (event.error === 'not-allowed') {
          reportMicError(
            'Speech recognition needs microphone access. Allow the mic for this site, then end and restart the session.'
          );
        } else if (event.error === 'network') {
          reportMicError(
            'Speech recognition needs a network connection (Chrome uses Google servers). Check connectivity and try again.'
          );
        } else if (event.error === 'audio-capture') {
          reportMicError(
            'No microphone detected or it is in use by another app. Close other apps using the mic and try again.'
          );
        }
      }
    };

    recognition.onend = () => {
      isRecognitionRunningRef.current = false;
      if (isActiveRef.current) {
        setTimeout(() => {
          // Never restart capture while assistant TTS is playing — simultaneous mic + speaker
          // causes many browsers to duck or clip TTS (often after the first sentence).
          if (
            isActiveRef.current &&
            !isRecognitionRunningRef.current &&
            recognitionRef.current &&
            !isSpeakingRef.current
          ) {
            try { recognitionRef.current.start(); } catch (e) { /* ignore */ }
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      pauseDetection.clearPauseTimer();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onresult = null;
          recognitionRef.current.stop();
        } catch (e) { /* ignore */ }
        recognitionRef.current = null;
      }
      isRecognitionRunningRef.current = false;
    };
  }, [useWebSpeech, pauseDuration, symptomLanguage.language]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // DEEPGRAM FLUX (English alternative to Web Speech API)
  // =========================================================================

  useEffect(() => {
    if (!useDeepgramFlux) return;

    const handleFluxTranscript = (data) => {
      if (openingInProgressRef.current) return;
      if (!data.transcript) return;
      if (isSpeakingRef.current && enableBargeInRef.current) {
        maybeInterruptForUserSpeech();
      }
      const canAccept = acceptingInputRef.current || (enableBargeInRef.current && isSpeakingRef.current);
      if (!canAccept) return;
      if (isSpeakingRef.current && !enableBargeInRef.current) return;
      if (echoFilter.isInCooldown()) return;
      if (isProcessingPauseRef.current) return;

      if (!audioConfirmedRef.current) {
        setAudioConfirmed(true);
        audioConfirmedRef.current = true;
      }

      // Accumulate; echo filter happens when end-of-turn fires
      transcriptAccumulatorRef.current = (transcriptAccumulatorRef.current + ' ' + data.transcript).trim();
      hasSpokeRecentlyRef.current = true;
      onLiveTranscriptRef.current?.(transcriptAccumulatorRef.current.trim());
    };

    const handleFluxEndOfTurn = async (data) => {
      if (isSpeakingRef.current && !enableBargeInRef.current) return;
      if (isProcessingPauseRef.current) return;
      if (!acceptingInputRef.current) {
        if (hasSpokeRecentlyRef.current) {
          pendingEndOfTurnRef.current = { timestamp: Date.now(), transcript: data.transcript };
        }
        return;
      }
      if (hasSpokeRecentlyRef.current && isActiveRef.current) {
        // Flush accumulated transcript through echo filter before processing
        const accumulated = transcriptAccumulatorRef.current || data.transcript || '';
        transcriptAccumulatorRef.current = '';
        interimTranscriptRef.current = '';
        onLiveTranscriptRef.current?.('');
        const cleaned = echoFilter.filterEcho(accumulated);
        if (cleaned && cleaned.trim()) {
          await addUserToLog(cleaned.trim());
        }
        if (handlePauseDetectedRef.current) handlePauseDetectedRef.current();
      }
    };

    const handleFluxTurnResumed = () => { /* user continued speaking */ };
    const handleFluxConnectionChange = (state) => { setIsFluxConnected(state === 'connected'); };

    deepgramFluxService.on(FLUX_EVENTS.TRANSCRIPT, handleFluxTranscript);
    deepgramFluxService.on(FLUX_EVENTS.END_OF_TURN, handleFluxEndOfTurn);
    deepgramFluxService.on(FLUX_EVENTS.TURN_RESUMED, handleFluxTurnResumed);
    deepgramFluxService.on('connectionStateChange', handleFluxConnectionChange);

    return () => {
      deepgramFluxService.off(FLUX_EVENTS.TRANSCRIPT, handleFluxTranscript);
      deepgramFluxService.off(FLUX_EVENTS.END_OF_TURN, handleFluxEndOfTurn);
      deepgramFluxService.off(FLUX_EVENTS.TURN_RESUMED, handleFluxTurnResumed);
      deepgramFluxService.off('connectionStateChange', handleFluxConnectionChange);
      if (deepgramFluxService.isConnected()) deepgramFluxService.disconnect();
    };
  }, [useDeepgramFlux, addUserToLog]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // SERVER STT (Deepgram Nova-3 streaming) — Classic STT path for Edge
  // =========================================================================

  // Reassigned every render so the Deepgram handlers (set once at connect time) always see
  // current closures. Mirrors the Web Speech `onresult` flow: same input gating, transcript
  // accumulation, live-transcript callback, and timer-based pause detection — so the rest of
  // the engine (pause handler → ask next question) behaves identically regardless of STT source.
  serverSttResultRef.current = (result, isFinal) => {
    if (openingInProgressRef.current) return;
    const text = String(result?.text || '').trim();
    if (isSpeakingRef.current && enableBargeInRef.current && text.length >= 3) {
      maybeInterruptForUserSpeech();
    }
    const canAccept = acceptingInputRef.current || (enableBargeInRef.current && isSpeakingRef.current);
    if (!canAccept) return;
    if (isSpeakingRef.current && !enableBargeInRef.current) return;
    if (echoFilter.isInCooldown()) return;
    if (isProcessingPauseRef.current) return;

    if (!text) return;

    if (!audioConfirmedRef.current) {
      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
    }

    const previousInterim = interimTranscriptRef.current;

    if (isFinal) {
      transcriptAccumulatorRef.current = (transcriptAccumulatorRef.current + ' ' + text).trim();
      interimTranscriptRef.current = '';
    } else {
      // Keep latest interim so short yes/no that never go final are still captured on pause.
      interimTranscriptRef.current = text;
    }
    hasSpokeRecentlyRef.current = true;

    const liveText = (transcriptAccumulatorRef.current + (interimTranscriptRef.current ? ' ' + interimTranscriptRef.current : '')).trim();
    onLiveTranscriptRef.current?.(liveText);

    const interimChanged =
      !isFinal &&
      Boolean(text) &&
      normalizePauseText(text) !== normalizePauseText(previousInterim);

    if (isFinal || interimChanged) {
      schedulePauseAfterSpeech(isFinal);
    }
  };

  // Open the mic and stream PCM to Deepgram Nova-3. Used only when useServerStt (Edge).
  const startServerStt = useCallback(async () => {
    if (deepgramSttStartingRef.current) return false;
    // Already running from an earlier start (e.g. resume) — just confirm audio.
    if (deepgramSTTService.isConnected && deepgramCaptureRef.current) {
      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
      return true;
    }

    deepgramSttStartingRef.current = true;
    try {
      const langBase = String(symptomLanguage.language || 'en-US').toLowerCase();
      // Nova-3: 'en-US' for English; 'multi' (auto language detection / code-switching) otherwise.
      const dgLanguage = langBase.startsWith('en') ? 'en-US' : 'multi';
      deepgramSTTService.configure({ language: dgLanguage, enableCodeSwitching: true });
      deepgramSTTService.setHandlers({
        onTranscript: (r) => serverSttResultRef.current?.(r, true),
        onInterimTranscript: (r) => serverSttResultRef.current?.(r, false),
        onError: (e) => { console.warn('[VoiceEngine] Deepgram STT error:', e); },
        onConnectionChange: () => { /* status handled via audioConfirmed */ },
      });

      const connected = await deepgramSTTService.connect();
      if (!connected) {
        console.warn('[VoiceEngine] Deepgram STT could not connect (Edge STT unavailable).');
        reportMicError(
          isWebSpeechSttUnreliable()
            ? 'Server speech recognition could not connect. If you are on Microsoft Edge, try Google Chrome for voice input, or ask your admin to deploy the deepgramToken function with a Deepgram API key.'
            : 'Server speech recognition could not connect. Try Chrome, or check your network and refresh.'
        );
        deepgramSttStartingRef.current = false;
        return false;
      }

      if (!deepgramCaptureRef.current) {
        deepgramCaptureRef.current = createDeepgramMicCapture({
          onAudioData: (buffer) => {
            if (!isActiveRef.current) return;
            const allowDuringTts = enableBargeInRef.current && isSpeakingRef.current;
            if (!acceptingInputRef.current && !allowDuringTts) return;
            if (isSpeakingRef.current && !allowDuringTts) return;
            if (deepgramSTTService.isConnected) deepgramSTTService.sendAudio(buffer);
          },
        });
      }
      await deepgramCaptureRef.current.start();

      setAudioConfirmed(true);
      audioConfirmedRef.current = true;
      deepgramSttStartingRef.current = false;
      return true;
    } catch (error) {
      console.warn('[VoiceEngine] Failed to start Deepgram STT (Edge):', error);
      reportMicError(
        'Could not start server speech recognition. Try Chrome, or allow microphone access and refresh.'
      );
      deepgramSttStartingRef.current = false;
      return false;
    }
  }, [symptomLanguage.language, isSpeakingRef, reportMicError]);

  // Release the mic and Deepgram socket for the server-STT path.
  const stopServerStt = useCallback(() => {
    try {
      deepgramCaptureRef.current?.stop();
    } catch (e) {
      console.warn('[VoiceEngine] Deepgram capture stop failed:', e);
    }
    deepgramCaptureRef.current = null;
    try {
      if (deepgramSTTService.isConnected) deepgramSTTService.disconnect();
    } catch (e) {
      console.warn('[VoiceEngine] Deepgram STT disconnect failed:', e);
    }
  }, []);

  // Always release the mic/socket on unmount.
  useEffect(() => () => stopServerStt(), [stopServerStt]);

  // =========================================================================
  // PAUSE HANDLER (delegates to consumer's onPauseDetected)
  // =========================================================================

  const handlePauseDetected = useCallback(async () => {
    if (isProcessingPauseRef.current) return;
    if (!hasSpokeRecentlyRef.current) {
      const pendingSpeech = (transcriptAccumulatorRef.current || interimTranscriptRef.current || '').trim();
      if (!pendingSpeech) return;
      hasSpokeRecentlyRef.current = true;
    }

    isProcessingPauseRef.current = true;
    setIsProcessingInput(true);
    clearUtteranceWatchdog();
    try {
      let latestUserUtterance = '';
      const deferredOverride = deferredTranscriptOverrideRef.current;
      if (deferredOverride) {
        deferredTranscriptOverrideRef.current = '';
        latestUserUtterance = deferredOverride;
        // Skip echo filter — deferred text was captured before TTS, not during
      } else {
        // Flush accumulated transcript (final results); fall back to interim for short yes/no that never go final
        let rawAccumulated = transcriptAccumulatorRef.current;
        transcriptAccumulatorRef.current = '';
        if (!rawAccumulated && interimTranscriptRef.current) {
          rawAccumulated = interimTranscriptRef.current;
          interimTranscriptRef.current = '';
        }
        if (rawAccumulated) {
          const cleaned = echoFilter.filterEcho(rawAccumulated);
          if (cleaned && cleaned.trim()) {
            latestUserUtterance = cleaned.trim();
            adapterRef.current.commitUserTurn(latestUserUtterance);
          } else {
            // Entire accumulated text was echo — nothing real to process
            onLiveTranscriptRef.current?.('');
            releasePauseMutex();
            return;
          }
        }
      }
      onLiveTranscriptRef.current?.('');

      const handler = onPauseDetectedRef.current;
      if (!handler) { releasePauseMutex(); return; }

      const result = await handler({
        userTranscript: latestUserUtterance,
        conversationLog: conversationLogRef.current,
        questionsAsked: questionsAskedRef.current,
        context: contextRef.current,
      });

      const deferredTranscript = String(result?.deferredUserTranscript || '').trim();
      if (deferredTranscript) {
        pendingDeferredTranscriptRef.current = deferredTranscript;
      }

      if (!result) { releasePauseMutex(); return; }

      const replayDeferredBrainTurnAfterTts = async () => {
        const deferredAfterTts = pendingDeferredTranscriptRef.current;
        if (!deferredAfterTts || !isActiveRef.current) return;
        pendingDeferredTranscriptRef.current = '';
        adapterRef.current.replayDeferredUserTurn(deferredAfterTts);
        deferredTranscriptOverrideRef.current = deferredAfterTts;
        hasSpokeRecentlyRef.current = true;
        releasePauseMutex();
        if (handlePauseDetectedRef.current) {
          await handlePauseDetectedRef.current();
        }
      };

      if (result.isComplete) {
        if (result.stopImmediately) {
          scheduleVerbalEndConversationAck(setEndConversationAcknowledged, () => {
            if (stopConversationRef.current) stopConversationRef.current();
          });
          releasePauseMutex();
          return;
        }
        if (result.question || (Array.isArray(result.speechSegments) && result.speechSegments.length)) {
          await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
          if (askQuestionRef.current) {
            await askQuestionRef.current(result.question, {
              speechSegments: result.speechSegments,
              preserveExactText: result.preserveExactText,
              deliveryProfile: result.deliveryProfile,
            });
          }
          await replayDeferredBrainTurnAfterTts();
        }
        // Auto-stop the conversation on completion
        if (stopConversationRef.current) stopConversationRef.current();
        releasePauseMutex();
        return;
      }

      if (result.question || (Array.isArray(result.speechSegments) && result.speechSegments.length)) {
        await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
        if (askQuestionRef.current) {
          await askQuestionRef.current(result.question, {
            speechSegments: result.speechSegments,
            preserveExactText: result.preserveExactText,
            deliveryProfile: result.deliveryProfile,
          });
        }
        await replayDeferredBrainTurnAfterTts();
      } else {
        // No question to speak — release mutex immediately
        releasePauseMutex();
      }
    } catch (err) {
      console.error('[VoiceEngine] handlePauseDetected error:', err);
      releasePauseMutex();
    }
  }, [echoFilter, clearUtteranceWatchdog, releasePauseMutex]);

  // Keep refs in sync with state
  useEffect(() => {
    addToLogRef.current = addToLog;
    questionsAskedRef.current = questionsAsked;
    handlePauseDetectedRef.current = handlePauseDetected;
  });
  useEffect(() => {
    conversationLogRef.current = conversationLog;
  }, [conversationLog]);

  // =========================================================================
  // ASK QUESTION
  // =========================================================================

  const askQuestion = useCallback(async (question, askOptions = {}) => {
    const {
      resumeListeningAfterSpeak = true,
      speechSegments,
      preserveExactText = false,
      deliveryProfile = '',
    } = askOptions;

    const segments =
      Array.isArray(speechSegments) && speechSegments.length > 0
        ? speechSegments.filter((s) => String(s?.text ?? '').trim())
        : [{ text: question, preserveExactText, deliveryProfile }];

    if (segments.length === 0) {
      releasePauseMutex();
      return;
    }

    bargeInAbortRef.current = false;
    setWasBargedIn(false);

    const logText = segments.map((s) => String(s.text).trim()).join(' ');

    setSpeaking(true);
    if (enableBargeInRef.current) {
      setIsListening(true);
    } else {
      setIsListening(false);
    }
    hasSpokeRecentlyRef.current = false;
    setCurrentQuestion(logText);
    pauseDetection.clearPauseTimer();

    if (!enableBargeInRef.current && !useDeepgramFlux && recognitionRef.current && isRecognitionRunningRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* ignore */
      }
      isRecognitionRunningRef.current = false;
    }

    const newQuestionsAsked = [...questionsAskedRef.current, logText];
    questionsAskedRef.current = newQuestionsAsked;
    setQuestionsAsked(newQuestionsAsked);

    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    safetyTimerRef.current = setTimeout(() => {
      safetyTimerRef.current = null;
      if (isProcessingPauseRef.current) {
        console.warn('[VoiceEngine] Safety timeout — releasing mutex after 15s');
        releasePauseMutex();
      }
    }, 15000);

    const finishAfterBargeIn = () => {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      setCurrentQuestion('');
      releasePauseMutex();
      bargeInAbortRef.current = false;
    };

    const finishAfterSpeak = () => {
      if (bargeInAbortRef.current) {
        finishAfterBargeIn();
        return;
      }
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      setCurrentQuestion('');
      hasSpokeRecentlyRef.current = false;
      pendingEndOfTurnRef.current = null;
      transcriptAccumulatorRef.current = '';
      interimTranscriptRef.current = '';
      onLiveTranscriptRef.current?.('');
      setSpeaking(false);
      releasePauseMutex();
      if (resumeListeningAfterSpeak) {
        setIsListening(true);
        if (!useDeepgramFlux && isActiveRef.current) {
          setTimeout(() => {
            if (!isActiveRef.current || isSpeakingRef.current) return;
            if (recognitionRef.current && !isRecognitionRunningRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                /* ignore */
              }
            }
          }, POST_TTS_WEB_SPEECH_RESUME_MS);
        }
      }
    };

    const handleSpeakError = () => {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      setSpeaking(false);
      setCurrentQuestion('');
      releasePauseMutex();
      if (resumeListeningAfterSpeak && !useDeepgramFlux && isActiveRef.current) {
        setTimeout(() => {
          if (!isActiveRef.current || isSpeakingRef.current) return;
          if (recognitionRef.current && !isRecognitionRunningRef.current) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              /* ignore */
            }
          }
        }, POST_TTS_WEB_SPEECH_RESUME_MS);
      }
    };

    try {
      for (let i = 0; i < segments.length; i++) {
        if (bargeInAbortRef.current) break;
        const seg = segments[i];
        const segText = String(seg.text ?? '').trim();
        if (!segText) continue;
        if (i > 0) await sleep(SPEECH_SEGMENT_GAP_MS);
        if (bargeInAbortRef.current) break;

        const segPreserveExact = Boolean(seg.preserveExactText ?? preserveExactText);
        const segDeliveryProfile = seg.deliveryProfile || deliveryProfile;
        const segResolved = segPreserveExact
          ? segText
          : await resolveAssistantTextForUser(segText);

        const segmentPosition =
          segments.length === 1
            ? 'only'
            : i === 0
              ? 'first'
              : i === segments.length - 1
                ? 'last'
                : 'middle';

        try {
          await speakWithPreferredProvider(segText, {
            resolvedText: segResolved,
            preserveExactText: segPreserveExact,
            deliveryProfile: segDeliveryProfile,
            segmentPosition,
            onEnd: segmentPosition === 'only' || segmentPosition === 'last' ? finishAfterSpeak : undefined,
            onError: segmentPosition === 'only' || segmentPosition === 'last' ? handleSpeakError : undefined,
          });
        } catch (speakError) {
          if (bargeInAbortRef.current || isPlaybackStoppedError(speakError)) {
            break;
          }
          throw speakError;
        }
        if (bargeInAbortRef.current) break;
      }
      if (bargeInAbortRef.current) {
        finishAfterBargeIn();
        return;
      }
      adapterRef.current.seedAssistantTurn(logText);
      adapterRef.current.commitAssistantTurn(logText);
    } catch (error) {
      if (bargeInAbortRef.current || isPlaybackStoppedError(error)) {
        finishAfterBargeIn();
        return;
      }
      console.error('[VoiceEngine] Failed to ask question:', error);
      handleSpeakError();
    }
  }, [
    resolveAssistantTextForUser,
    speakWithPreferredProvider,
    pauseDetection,
    setSpeaking,
    setQuestionsAsked,
    useDeepgramFlux,
    isSpeakingRef,
  ]);

  askQuestionRef.current = askQuestion;

  // Request first question immediately after opening (no user response required). Used for check-in.
  const requestFirstQuestionAfterOpening = useCallback(async () => {
    if (isProcessingPauseRef.current) return;
    const handler = onPauseDetectedRef.current;
    if (!handler) return;

    isProcessingPauseRef.current = true;
    try {
      const result = await handler({
        userTranscript: '',
        conversationLog: conversationLogRef.current,
        questionsAsked: questionsAskedRef.current,
        context: contextRef.current,
      });
      if (result?.question) {
        await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
        if (askQuestionRef.current) await askQuestionRef.current(result.question);
      }
    } catch (err) {
      console.error('[VoiceEngine] requestFirstQuestionAfterOpening error:', err);
    } finally {
      releasePauseMutex();
    }
  }, []);

  const requestOpeningDisclaimer = useCallback(async ({ resumeListeningAfterSpeak = true } = {}) => {
    if (!askDisclaimerAfterOpeningRef.current) return;
    if (isProcessingPauseRef.current) return;
    const handler = onPauseDetectedRef.current;
    if (!handler) return;

    isProcessingPauseRef.current = true;
    try {
      const result = await handler({
        userTranscript: '',
        conversationLog: conversationLogRef.current,
        questionsAsked: questionsAskedRef.current,
        context: contextRef.current,
      });
      if (result?.question) {
        await new Promise(resolve => setTimeout(resolve, RESPONSE_DELAY_MS));
        if (askQuestionRef.current) {
          await askQuestionRef.current(result.question, { resumeListeningAfterSpeak });
        }
      }
    } catch (err) {
      console.error('[VoiceEngine] requestOpeningDisclaimer error:', err);
    } finally {
      releasePauseMutex();
    }
  }, []);

  // Clear safety timer on unmount (Leak-Proof)
  useEffect(() => {
    return () => {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    };
  }, []);

  // =========================================================================
  // START / STOP
  // =========================================================================

  const startConversation = useCallback(async (overrides = {}) => {
    unlockAudioContext();
    if (isActiveRef.current || hasStartedRef.current) return;

    clearMicError();

    if (!speechSupported) {
      reportMicError(
        'Voice input is not supported in this browser. Use Chrome or Edge on desktop, then try again.'
      );
      return;
    }

    const micOk = await ensureMicrophoneAccess();
    if (!micOk) return;

    // Cross-feature lock so PageGuide (and peers) can detect an active session.
    acquireVoiceSession('voiceConversationEngine');

    hasStartedRef.current = true;
    setEndConversationAcknowledged(false);
    setIsActive(true);
    isActiveRef.current = true;
    setIsStarting(true);

    // Check if resuming existing conversation (controlled state has data)
    const hasExistingConversation = questionsAsked.length > 0 || conversationLog.length > 0;

    // Initialize Deepgram Flux if needed
    if (useDeepgramFlux) {
      if (deepgramFluxService.isConnected()) {
        setIsFluxConnected(true);
      } else {
        try {
          const connected = await deepgramFluxService.connect();
          if (connected) setIsFluxConnected(true);
        } catch (error) {
          console.warn('[VoiceEngine] Flux connection error:', error);
        }
      }
    }

    // Only bail when we intend to use Web Speech but it isn't available. On Edge
    // (useServerStt) there is no recognition object — the Deepgram path handles STT.
    if (!useDeepgramFlux && !useServerStt && !recognitionRef.current) {
      hasStartedRef.current = false;
      setIsActive(false);
      isActiveRef.current = false;
      setIsStarting(false);
      reportMicError(
        'Speech recognition is unavailable in this browser. Use Chrome or Edge and try again.'
      );
      return;
    }

    if (hasExistingConversation) {
      // RESUMING - don't reset state, don't speak opening prompt
      questionsAskedRef.current = questionsAsked;
      setSpeaking(false);
      acceptingInputRef.current = true;
      setIsAcceptingInput(true);

      if (useServerStt) {
        // Edge: resume capturing via Deepgram streaming.
        await startServerStt();
        setIsListening(true);
      } else if (recognitionRef.current) {
        try {
          if (isRecognitionRunningRef.current) {
            setIsListening(true);
          } else {
            recognitionRef.current.start();
            setIsListening(true);
          }
        } catch (e) {
          console.error('[VoiceEngine] Failed to resume listening:', e);
          isRecognitionRunningRef.current = false;
        }
      }
      setIsStarting(false);
      return;
    }

    // FRESH START - reset state and speak disclaimer (if any) then opening prompt
    setQuestionsAsked([]);
    questionsAskedRef.current = [];
    setConversationLog([]);
    conversationLogRef.current = [];
    acceptingInputRef.current = false;
    setIsAcceptingInput(false);

    // Pre-start STT capture
    if (useServerStt) {
      const sttOk = await startServerStt();
      if (!sttOk) {
        hasStartedRef.current = false;
        setIsActive(false);
        isActiveRef.current = false;
        setIsStarting(false);
        stopServerStt();
        return;
      }
    } else if (recognitionRef.current && !isRecognitionRunningRef.current) {
      try { recognitionRef.current.start(); } catch (e) { /* ignore */ }
      setTimeout(() => {
        if (isActiveRef.current && !audioConfirmedRef.current && isRecognitionRunningRef.current) {
          setAudioConfirmed(true);
          audioConfirmedRef.current = true;
        }
      }, 3000);
    }

    // Pause STT capture while assistant TTS plays unless barge-in keeps the mic open.
    if (!enableBargeInRef.current && !useDeepgramFlux && recognitionRef.current && isRecognitionRunningRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* ignore */
      }
      isRecognitionRunningRef.current = false;
    }

    if (askDisclaimerAfterOpeningRef.current) {
      await requestOpeningDisclaimer({ resumeListeningAfterSpeak: false });
      if (!enableBargeInRef.current && !useDeepgramFlux && recognitionRef.current && isRecognitionRunningRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          /* ignore */
        }
        isRecognitionRunningRef.current = false;
      }
    }

    bargeInAbortRef.current = false;
    setWasBargedIn(false);

    openingInProgressRef.current = true;

    // Always pause STT during opening — barge-in must not listen to speaker bleed.
    if (!useDeepgramFlux && recognitionRef.current && isRecognitionRunningRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        /* ignore */
      }
      isRecognitionRunningRef.current = false;
    }
    if (useServerStt) {
      stopServerStt();
    }

    // Speak opening
    const openingText =
      typeof overrides.openingMessage === 'string' && overrides.openingMessage.trim()
        ? overrides.openingMessage.trim()
        : openingMessageRef.current;
    const openingTextForUser = await resolveAssistantTextForUser(openingText);
    setSpeaking(true);
    setIsListening(false);
    adapterRef.current.commitAssistantTurn(openingTextForUser || openingText);

    const startRecognitionAfterTTS = () => {
      openingInProgressRef.current = false;
      if (bargeInAbortRef.current) {
        bargeInAbortRef.current = false;
        return;
      }
      setSpeaking(false);
      pendingEndOfTurnRef.current = null;
      const resumeDelay = Math.max(ECHO_COOLDOWN_MS, POST_TTS_WEB_SPEECH_RESUME_MS) + 100;
      setTimeout(() => {
        if (!isActiveRef.current) return;
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
        if (useServerStt) {
          void startServerStt().then((ok) => {
            if (ok) {
              setIsListening(true);
              setAudioConfirmed(true);
              audioConfirmedRef.current = true;
            }
          });
          return;
        }
        if (!recognitionRef.current) return;
        const doStart = () => {
          try {
            if (isRecognitionRunningRef.current) {
              setIsListening(true);
            } else {
              recognitionRef.current.start();
              setIsListening(true);
            }
          } catch (e) {
            isRecognitionRunningRef.current = false;
            reportMicError(
              'Could not restart speech recognition after the opening message. End the session and try again.'
            );
          }
        };
        if (useDeepgramFlux) {
          doStart();
        } else {
          doStart();
        }
      }, resumeDelay);
    };

    const speakWithBrowserFallback = (text) => {
      if (!allowBrowserTtsFallbackRef.current) {
        console.warn('[VoiceEngine] Opening TTS failed; browser fallback disabled.');
        setIsStarting(false);
        setSpeaking(false);
        reportMicError(
          'Voice playback failed. Check your connection and refresh, then start the session again.'
        );
        hasStartedRef.current = false;
        setIsActive(false);
        isActiveRef.current = false;
        openingInProgressRef.current = false;
        return;
      }
      if (!('speechSynthesis' in window)) {
        setIsStarting(false);
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
        startRecognitionAfterTTS();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.onstart = () => { setIsStarting(false); setSpeaking(true); };
      utterance.onend = () => {
        setTimeout(async () => {
          acceptingInputRef.current = true;
          setIsAcceptingInput(true);
          if (askFirstQuestionAfterOpeningRef.current) {
            await requestFirstQuestionAfterOpening();
          }
          startRecognitionAfterTTS();
        }, 1000);
      };
      utterance.onerror = () => {
        setIsStarting(false);
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
        startRecognitionAfterTTS();
      };
      window.speechSynthesis.speak(utterance);
    };

    let fallbackStarted = false;
    try {
      if (
        prefetchedOpeningAudioRef.current &&
        (ttsProviderRef.current === 'openai' ||
          ttsProviderRef.current === 'gemini_flash_tts') &&
        prefetchedOpeningTextRef.current === (openingTextForUser || openingText)
      ) {
        echoFilter.addSystemUtterance(openingTextForUser || openingText);
        if (!enableBargeInRef.current) {
          acceptingInputRef.current = false;
          setIsAcceptingInput(false);
        }
        echoFilter.startEchoCooldown();

        await playPrefetchedAudio(prefetchedOpeningAudioRef.current, {
          onStart: () => { setIsStarting(false); setSpeaking(true); },
          onEnd: () => {
            if (bargeInAbortRef.current) return;
            echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
            setTimeout(async () => {
              acceptingInputRef.current = true;
              setIsAcceptingInput(true);
            }, ECHO_COOLDOWN_MS);
            void (async () => {
              if (askFirstQuestionAfterOpeningRef.current) {
                await requestFirstQuestionAfterOpening();
              }
              startRecognitionAfterTTS();
            })();
          },
          onError: () => {
            echoFilter.endEchoCooldown(ECHO_COOLDOWN_MS);
            setTimeout(() => { acceptingInputRef.current = true; setIsAcceptingInput(true); }, ECHO_COOLDOWN_MS);
            if (!fallbackStarted) { fallbackStarted = true; speakWithBrowserFallback(openingTextForUser || openingText); }
          },
        });
        prefetchedOpeningAudioRef.current = null;
        prefetchedOpeningTextRef.current = '';
      } else {
        await speakWithPreferredProvider(openingText, {
          resolvedText: openingTextForUser,
          deliveryProfile: 'clinical-narration',
          phase: 'opening',
          onStart: () => { setIsStarting(false); setSpeaking(true); },
          onEnd: () => {
            if (bargeInAbortRef.current) return;
            void (async () => {
              if (askFirstQuestionAfterOpeningRef.current) {
                await requestFirstQuestionAfterOpening();
              }
              startRecognitionAfterTTS();
            })();
          },
          onError: (error) => {
            console.error('[VoiceEngine] Opening TTS error:', error);
            setIsStarting(false);
            if (!fallbackStarted) { fallbackStarted = true; speakWithBrowserFallback(openingTextForUser || openingText); }
          },
        });
      }
    } catch (error) {
      console.error('[VoiceEngine] TTS failed:', error);
      if (!fallbackStarted) { fallbackStarted = true; speakWithBrowserFallback(openingTextForUser || openingText); }
    }
  }, [addToLog, questionsAsked, conversationLog, setQuestionsAsked, setConversationLog, speakWithPreferredProvider, echoFilter, useDeepgramFlux, useServerStt, startServerStt, stopServerStt, setSpeaking, requestFirstQuestionAfterOpening, requestOpeningDisclaimer, resolveAssistantTextForUser, speechSupported, ensureMicrophoneAccess, reportMicError, clearMicError]);

  const stopConversation = useCallback(() => {
    bargeInAbortRef.current = false;
    setWasBargedIn(false);
    setIsActive(false);
    isActiveRef.current = false;
    setIsStarting(false);
    setIsListening(false);
    setSpeaking(false);
    hasStartedRef.current = false;
    isRecognitionRunningRef.current = false;
    openingInProgressRef.current = false;
    setAudioConfirmed(false);
    audioConfirmedRef.current = false;
    clearMicError();
    releaseVoiceSession('voiceConversationEngine');

    pauseDetection.clearPauseTimer();
    clearUtteranceWatchdog();
    releasePauseMutex();
    pendingDeferredTranscriptRef.current = '';
    deferredTranscriptOverrideRef.current = '';

    // Flush any pending user transcript into the log so "End Conversation" captures everything
    const pendingText = transcriptAccumulatorRef.current;
    transcriptAccumulatorRef.current = '';
    interimTranscriptRef.current = '';
    onLiveTranscriptRef.current?.('');
    if (pendingText && pendingText.trim()) {
      const cleaned = echoFilter.filterEcho(pendingText);
      if (cleaned && cleaned.trim()) {
        adapterRef.current.commitUserTurn(cleaned.trim());
      }
    }

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
    }

    // Edge server-STT path: release mic + Deepgram socket.
    stopServerStt();

    if (isFluxConnected && deepgramFluxService.isConnected()) {
      deepgramFluxService.disconnect();
      setIsFluxConnected(false);
    }

    try {
      careStartStopSpeaking();
    } catch (e) {
      /* ignore */
    }

    try {
      geminiFlashTTSService.stop();
    } catch (e) {
      /* ignore */
    }

    try {
      openAIStopSpeaking();
    } catch (e) {
      console.warn('[VoiceEngine] openAIStopSpeaking failed:', e);
    }

    try {
      deepgramTTSService.stop();
    } catch (e) {
      console.warn('[VoiceEngine] deepgramTTSService.stop failed:', e);
    }

    if (audioRef.current && typeof audioRef.current.pause === 'function') {
      audioRef.current.pause();
    }
    audioRef.current = null;

    onComplete?.(conversationLogRef.current);
  }, [isFluxConnected, onComplete, pauseDetection, setSpeaking, echoFilter, stopServerStt, clearMicError]);

  // Keep stopConversationRef in sync
  useEffect(() => { stopConversationRef.current = stopConversation; }, [stopConversation]);

  return {
    // State
    isActive,
    isStarting,
    isListening,
    isSpeaking,
    currentQuestion,
    questionsAsked,
    conversationLog,
    audioConfirmed,
    isAcceptingInput,
    isProcessingInput,
    speechSupported,
    micError,
    wasBargedIn,
    isFluxConnected,
    endConversationAcknowledged,

    // Actions
    startConversation,
    stopConversation,
    askQuestion,
    addToLog,
    clearWasBargedIn,

    // Refs for UI
    conversationScrollRef,

    // Language
    language: symptomLanguage.language,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
};

export default useVoiceConversationEngine;
